// TokenManager.js
import { Preferences } from '@capacitor/preferences';
import { isCapacitor } from '../utils/platform';
import { secureTokenStorage } from './SecureTokenStorage';
import { tokenLog, tokenWarn, tokenError } from './Debug';

const CHANNEL_NAME = 'auth_channel';

// Simple mutex implementation for token operations
class TokenOperationMutex {
  constructor() {
    this.locked = false;
    this.queue = [];
  }

  async acquire() {
    return new Promise((resolve) => {
      if (!this.locked) {
        this.locked = true;
        resolve();
      } else {
        this.queue.push(resolve);
      }
    });
  }

  release() {
    if (this.queue.length > 0) {
      const resolve = this.queue.shift();
      resolve();
    } else {
      this.locked = false;
    }
  }
}

class TokenManager {
  constructor() {
    this.accessToken = null;
    this.expiration = 0;
    this.channel = null;
    this.storageError = false;
    this.operationLock = new TokenOperationMutex();
    this.initializationPromise = null;
    // Don't init() in constructor for Capacitor async operations
    if (!isCapacitor) {
      this.init();
    } else {
      // For Capacitor, init after component mounts
      this.initAsync();
    }
  }
  
  async init() {
    // Load tokens based on platform
    if (isCapacitor) {
      // For Capacitor, load from Preferences
      try {
        const { value: token } = await Preferences.get({ key: 'access_token' });
        const { value: expStr } = await Preferences.get({ key: 'access_token_exp' });
        
        if (token) {
          this.accessToken = token;
          this.expiration = expStr ? parseInt(expStr) : 0;
          tokenLog('init() restored token from Preferences (Capacitor)', {
            hasToken: !!token,
            exp: this.expiration
          });
        }
      } catch (error) {
        tokenError('init() Error loading tokens from Preferences', error);
      }
    } else {
      // For web, load from localStorage
      const stored = localStorage.getItem('accessToken');
      const exp = +localStorage.getItem('accessTokenExp') || 0;
      if (stored) {
        this.accessToken = stored;
        this.expiration = exp;
        tokenLog('init() restored token from localStorage (web)', {
          hasToken: !!stored,
          exp: this.expiration
        });
      }
      
      // Setup BroadcastChannel only for web (multi-tab sync)
      this.setupBroadcastChannel();
    }
  }

  async initAsync() {
    // Prevent multiple simultaneous initializations
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      if (isCapacitor) {
        try {
          // First try to get from regular Preferences
          const { value: token } = await Preferences.get({ key: 'access_token' });
          const { value: exp } = await Preferences.get({ key: 'access_token_exp' });
          
          let restoredFromSecure = false;
          
          if (token && exp) {
            const expiration = parseInt(exp);
            const now = Date.now();
            
            // Check if token is still valid (with 1-minute buffer)
            if (expiration > now + 60000) {
              this.accessToken = token;
              this.expiration = expiration;
              tokenLog('initAsync() loaded valid token from Preferences', { exp: expiration });
            } else {
              // Preserve token in memory to allow refresh flow during initialization
              this.accessToken = token;
              this.expiration = expiration;
              tokenWarn('initAsync() loaded expiring/expired token from Preferences; will rely on refresh', { exp: expiration });
            }
          } else {
            // Try to restore from secure storage
            const secureTokens = await secureTokenStorage.getSecureTokens();
            if (secureTokens && secureTokens.accessToken) {
              const now = Date.now();
              if (secureTokens.expiration > now + 60000) {
                // Restore tokens
                this.accessToken = secureTokens.accessToken;
                this.expiration = secureTokens.expiration;
                
                // Also restore to regular storage
                await Preferences.set({ key: 'access_token', value: secureTokens.accessToken });
                await Preferences.set({ key: 'access_token_exp', value: String(secureTokens.expiration) });
                
                if (secureTokens.refreshToken) {
                  await Preferences.set({ key: 'refresh_token', value: secureTokens.refreshToken });
                }
                
                restoredFromSecure = true;
                tokenLog('initAsync() restored tokens from secure storage backup', { exp: this.expiration });
              }
            }
          }
        } catch (error) {
          tokenError('initAsync() error', error);
        }
      }
    })();

    return this.initializationPromise;
  }

  setupBroadcastChannel() {
    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (e) => {
        const { type, token, exp } = e.data || {};
        if (type === 'TOKENS_UPDATED') {
          this.accessToken = token;
          this.expiration = exp;
          localStorage.setItem('accessToken', token);
          localStorage.setItem('accessTokenExp', String(exp));
          tokenLog('Broadcast TOKENS_UPDATED received (web)', { exp });
        } else if (type === 'LOGOUT') {
          this.clearLocal();
          tokenLog('Broadcast LOGOUT received (web)');
        }
      };
    } catch (err) {
      // BroadcastChannel not supported (older browsers)
      tokenWarn('BroadcastChannel unavailable', err);
    }
  }

  broadcastUpdate() {
    // Only broadcast on web
    if (!isCapacitor && this.channel) {
      this.channel.postMessage({
        type: 'TOKENS_UPDATED',
        token: this.accessToken,
        exp: this.expiration
      });
    }
  }

  async setToken(token, expiresInSec, retryCount = 0) {
    // Acquire lock for token operations
    await this.operationLock.acquire();
    
    try {
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 1000;
      
      this.accessToken = token;
      
      // Decode JWT token to get server's expiration time
      let serverExpiration = null;
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.exp) {
            // Convert server expiration (seconds since epoch) to milliseconds
            serverExpiration = payload.exp * 1000;
          }
        }
        tokenLog('setToken() decoded JWT token', { serverExpiration });
      } catch (error) {
        tokenError('[TokenManager] Error decoding JWT token:', error);
      }
      
      // Use server expiration if available, otherwise fallback to provided expiresInSec
      if (serverExpiration) {
        this.expiration = serverExpiration;
      } else {
        // Fallback: Default to 24 hours if not specified (matching backend)
        const expiresIn = expiresInSec || 86400; // 24 hours in seconds
        this.expiration = Date.now() + expiresIn * 1000;
      }

      if (isCapacitor) {
        // Store in Capacitor Preferences with retry mechanism
        try {
          // Store tokens with verification
          await Preferences.set({ key: 'access_token', value: token });
          await Preferences.set({ key: 'access_token_exp', value: String(this.expiration) });
          
          // Wait a bit for iOS storage to settle
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Verify storage immediately
          const { value: storedToken } = await Preferences.get({ key: 'access_token' });
          const { value: storedExp } = await Preferences.get({ key: 'access_token_exp' });
          
          if (storedToken !== token || storedExp !== String(this.expiration)) {
            throw new Error('Token verification failed - stored values do not match');
          }

          // Also store in secure storage for iOS
          const refreshToken = await this.getStoredRefreshToken();
          if (refreshToken) {
            await secureTokenStorage.storeSecureTokens(token, refreshToken, this.expiration);
          }
          tokenLog('setToken() stored token successfully', { exp: this.expiration });
        } catch (error) {
          tokenError(`setToken() error saving tokens to Preferences (attempt ${retryCount + 1}/${MAX_RETRIES})`, error);
          
          // Retry logic with exponential backoff
          if (retryCount < MAX_RETRIES - 1) {
            const delay = RETRY_DELAY * Math.pow(2, retryCount);
            await new Promise(resolve => setTimeout(resolve, delay));
            return this.setToken(token, expiresInSec, retryCount + 1);
          } else {
            tokenError('setToken() failed after all retries');
            this.storageError = true;
            throw new Error('Token storage failed after all retries');
          }
        }
      } else {
        // Store in localStorage for web/electron
        localStorage.setItem('accessToken', token);
        localStorage.setItem('accessTokenExp', String(this.expiration));
        this.broadcastUpdate();
        tokenLog('setToken() stored token (web/electron)', { 
          exp: this.expiration,
          serverExpiration: serverExpiration
        });
      }
    } finally {
      // Always release the lock
      this.operationLock.release();
    }
  }

  getToken() {
    // If we're still initializing on Capacitor, return null
    if (isCapacitor && this.initializationPromise && !this.accessToken) {
      tokenWarn('getToken called during initialization, returning null');
      return null;
    }
    return this.accessToken;
  }

  async getStoredRefreshToken() {
    if (isCapacitor) {
      const { value } = await Preferences.get({ key: 'refresh_token' });
      return value;
    }
    return null;
  }

  isExpiringSoon(bufferMs = 60000) {
    // If still initializing, consider as expiring to trigger proper flow
    if (isCapacitor && this.initializationPromise && !this.accessToken) {
      return true;
    }
    
    if (!this.accessToken || !this.expiration) {
      tokenWarn('isExpiringSoon(): no token or expiration');
      return true;
    }
    
    const now = Date.now();
    const timeUntilExpiry = this.expiration - now;
    const isExpiring = now > (this.expiration - bufferMs);
    

    return isExpiring;
  }

  async clearLocal() {
    // Acquire lock for token operations
    await this.operationLock.acquire();
    
    try {
      this.accessToken = null;
      this.expiration = 0;
      
      if (isCapacitor) {
        // Clear from Capacitor Preferences
        try {
          await Preferences.remove({ key: 'access_token' });
          await Preferences.remove({ key: 'access_token_exp' });
          
          // Wait for iOS storage to settle
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Verify removal
          const { value: accessCheck } = await Preferences.get({ key: 'access_token' });
          const { value: expCheck } = await Preferences.get({ key: 'access_token_exp' });
          
          if (accessCheck || expCheck) {
            tokenWarn('clearLocal(): tokens still present after removal attempt');
          }
        } catch (error) {
          tokenError('clearLocal(): error clearing tokens from Preferences', error);
        }
      } else {
        // Clear from localStorage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('accessTokenExp');
      }
    } finally {
      // Always release the lock
      this.operationLock.release();
    }
  }

  async clear() {
    await this.clearLocal();
    
    // Broadcast logout only on web
    if (!isCapacitor && this.channel) {
      this.channel.postMessage({ type: 'LOGOUT' });
    }
    tokenLog('clear(): tokens cleared and logout broadcasted if web');
  }
}

export const tokenManager = new TokenManager();